import { useSearchParams } from '@solidjs/router'
import {
    api_admin_helmet_extension_requests_count_get,
    api_admin_helmet_extension_requests_list,
    api_admin_helmet_extension_requests_patch,
    api_admin_users_bulk_post,
    api_companies_helmet_extension_requests_count_get,
    api_companies_helmet_extension_requests_list,
    api_companies_users_bulk_post,
    Count,
    HelmetExtensionRequest,
    HelmetExtensionRequestState,
    User,
} from 'abi'
import { addAlert, Counter, HoverInp, LoadingDots, setPopup } from 'components'
import {
    CalendarIcon,
    CheckFillIcon,
    CloseIcon,
    CorpsIcon,
    LoadingIcon,
    QuestionIcon2,
    ShieldCheckIcon,
    UserIcon,
    WarningIcon,
} from 'icons'
import { HelmetState_FA } from 'pages/panel/extentions/requests'
import {
    Component,
    createEffect,
    For,
    JSX,
    on,
    onCleanup,
    Show,
} from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'
import { deepcopy } from 'utils/tools'
import { cState, isAdmin } from '../shared'

import './style/extentions.scss'

type REQ = Omit<HelmetExtensionRequest, 'user' | 'company'> & {
    user: User | null
}

type STATE = {
    count: Count
    page: number

    helmet: number | null

    state: HelmetExtensionRequestState | null

    loadingC: boolean
    loadingR: boolean
    error: boolean

    reqs: REQ[]

    activeReq: REQ | null
}

const STATE_DEF: STATE = {
    page: 0,
    count: {
        max_page: -1,
        total: -1,
    },
    helmet: null,

    state: null,

    loadingC: true,
    loadingR: true,
    error: false,

    reqs: [],
    activeReq: null,
}

const [state, setState] = createStore<STATE>(deepcopy(STATE_DEF))

const CorpExtentions: Component = () => {
    const [search_params, setSearchParams] = useSearchParams()

    createEffect(
        on(
            () => cState.corp?.id,
            id => {
                if (id === undefined) return

                let p = parseInt(search_params.page || '0')
                if (p !== 0) {
                    setState('page', p)
                }

                let page = unwrap(state.page)
                setSearchParams({ page })

                let ac = new AbortController()

                getCount(id)
                searchReqs(id)

                window.addEventListener('popstate', handlePopstate, {
                    signal: ac.signal,
                })

                onCleanup(() => ac.abort())
            }
        )
    )

    const handlePopstate = () => {
        let page = parseInt(search_params.page || '0')

        setState('page', page)
        cState.corp?.id && searchReqs(cState.corp.id)
    }

    const getCount = async (company: number) => {
        setState('loadingC', true)

        let res
        if (isAdmin()) {
            res = await api_admin_helmet_extension_requests_count_get({
                company,
            })
        } else {
            res = await api_companies_helmet_extension_requests_count_get({
                cid: company,
            })
        }

        setState('loadingC', false)

        if (!res.ok()) return

        setState('count', res.body)
    }
    const searchReqs = async (company?: number) => {
        if (!company) {
            if (cState.corp?.id === undefined) return

            company = cState.corp.id
        }

        let copy = deepcopy(state)

        let s = copy.state
        let helmet = copy.helmet || null

        let page = copy.page

        setState({
            loadingR: true,
            error: false,
        })

        let res

        if (isAdmin()) {
            res = await api_admin_helmet_extension_requests_list({
                page,
                company,
                helmet,
                state: s,
            })
        } else {
            res = await api_companies_helmet_extension_requests_list({
                page,
                cid: company,
                state: s,
                order: 'asc',
            })
        }

        if (!res.ok()) return setState({ loadingR: false, error: true })

        // let comps = res.body.map(({ company }) => company)
        let users = res.body.map(({ user }) => user)

        let usersRes
        // let compsRes

        if (isAdmin()) {
            usersRes = await api_admin_users_bulk_post({ ids: users })
        } else {
            usersRes = await api_companies_users_bulk_post(
                { cid: company },
                { ids: users }
            )
        }

        if (!usersRes.ok()) return setState({ loadingR: false, error: true })

        let result: REQ[] = []

        res.body.forEach(r => {
            let uId = usersRes.body.findIndex(u => u.id == r.user)

            let final: REQ = { ...r, user: null }

            if (uId !== -1) {
                final['user'] = usersRes.body[uId]!
            }

            result.push(final)
        })

        setState({ reqs: result, loadingR: false })
    }

    onCleanup(() => {
        setState(deepcopy(STATE_DEF))
    })

    interface StateFilterProps {
        icon: JSX.Element
        holder: string

        class?: string

        state: HelmetExtensionRequestState
    }
    const StateFilter: Component<StateFilterProps> = C => {
        return (
            <div
                class={`counter ${C.class || ''}`}
                role='button'
                onclick={() => {
                    if (state.state == C.state) setState('state', null)
                    else setState('state', C.state)

                    searchReqs()
                }}
                classList={{ active: C.state == state.state }}
            >
                <div class='icon'>{C.icon}</div>
                <p class='holder title_smaller'>{C.holder}</p>
            </div>
        )
    }

    const HelmetReqCmp: Component<REQ> = H => {
        return (
            <div
                class='helmet-req-cmp'
                role='button'
                onclick={() => {
                    setState('activeReq', deepcopy(H))
                }}
                classList={{
                    [H.state]: true,
                }}
            >
                <div class='req-head'>
                    <div class='head-right head-col'>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <CorpsIcon />
                                <span>شرکت:</span>
                            </div>
                            <div>{cState.corp?.name || 'بدون نام'}</div>
                        </div>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <UserIcon />
                                <span>کاربر:</span>
                            </div>
                            <div>{H.user?.name || 'بدون نام'}</div>
                        </div>
                        <div class='row title_smaller'>
                            <div class='holder'>
                                <ShieldCheckIcon />
                                <span>بیمه:</span>
                            </div>
                            <div>{H.helmet}</div>
                        </div>
                    </div>
                    <div class='head-left head-col'>
                        <div class='row title_smaller'>
                            {new Date(H.created_at * 1e3).toLocaleDateString(
                                'fa-IR'
                            )}
                            <div class='holder  icon'>
                                <CalendarIcon />
                            </div>
                        </div>
                        <div
                            class='row title_smaller'
                            classList={{ show: H.state !== 'pending' }}
                        >
                            <span>{HelmetState_FA[H.state]}</span>
                            <div
                                class='holder icon'
                                classList={{ [H.state]: true }}
                            >
                                <Show
                                    when={
                                        H.state !== 'rejected' &&
                                        H.state !== 'done'
                                    }
                                    fallback={
                                        <Show
                                            when={H.state == 'done'}
                                            fallback={<CloseIcon />}
                                        >
                                            <CheckFillIcon />
                                        </Show>
                                    }
                                >
                                    <div class='dot-pulse'></div>
                                </Show>
                            </div>
                        </div>
                    </div>
                </div>
                <div class='detail-container'>
                    <div class='req-detail title_smaller'>{H.detail}</div>
                </div>
            </div>
        )
    }

    return (
        <div class='corp-extentions'>
            <div class='extentions-wrapper'>
                <div
                    class='total-wrapper title_small'
                    classList={{ active: !state.loadingC }}
                >
                    <Counter end={state.count.total} />
                    <span>درخواست ثبت شده</span>
                </div>

                <div class='search-inps'>
                    <HoverInp
                        class='helmet-inp'
                        Icon={<CorpsIcon />}
                        holder='شماره بیمه'
                        acitve={state.helmet !== null}
                        inpType='number'
                        onInp={v => {
                            setState('helmet', parseInt(v || '0'))
                            searchReqs()
                        }}
                        value={String(state.helmet || '')}
                        showReset={state.helmet !== null}
                        onReset={() => {
                            setState('helmet', null)
                            searchReqs()
                        }}
                    />
                </div>
                <div class='state-filters'>
                    <StateFilter
                        state='done'
                        holder='حل شده'
                        icon={<CheckFillIcon />}
                        class='green'
                    />
                    <StateFilter
                        state='in_progress'
                        holder='درحال حل'
                        icon={<LoadingIcon />}
                    />
                    <StateFilter
                        state='pending'
                        holder='بدون وضعیت'
                        icon={<QuestionIcon2 />}
                        class='grey'
                    />
                    <StateFilter
                        state='rejected'
                        holder='رد شده'
                        icon={<CloseIcon />}
                        class='red'
                    />
                </div>

                <div class='reqs-wrapper'>
                    <Show when={!state.loadingR} fallback={<LoadingR />}>
                        <Show when={!state.error} fallback={<ErrorR />}>
                            <Show
                                when={state.reqs.length > 0}
                                fallback={<ErrorR empty />}
                            >
                                <For each={state.reqs}>
                                    {r => <HelmetReqCmp {...r} />}
                                </For>
                            </Show>
                        </Show>
                    </Show>
                </div>
            </div>

            <Show when={state.activeReq}>
                <ReqPopup />
            </Show>
        </div>
    )
}

const LoadingR = () => {
    return (
        <div class='reqs-loading title_hero'>
            <img src='/public/imgs/logo.png' decoding='async' alt='' />
            <div>در حال بارگذاری...</div>
        </div>
    )
}

const ErrorR: Component<{ empty?: boolean }> = P => {
    return (
        <div class='reqs-loading title_hero'>
            <CloseIcon />
            <div>
                <Show when={P.empty} fallback={'خطا در گرفتن اطلاعات'}>
                    درخواست ای با این مشخصات یافت نشد!
                </Show>
            </div>
        </div>
    )
}

const ReqPopup: Component = () => {
    type DATA = {
        loading: boolean
        error: boolean
    }
    const [data, setData] = createStore<DATA>({
        error: false,
        loading: false,
    })

    const close = () => {
        if (data.loading) return

        setState('activeReq', null)
    }

    const req = state.activeReq

    const submit = async (state: HelmetExtensionRequestState) => {
        if (!isAdmin()) return

        setData('loading', true)

        let id = unwrap(req!.id)

        let res = await api_admin_helmet_extension_requests_patch(
            { id },
            {
                state,
            }
        )
        setData('loading', false)

        if (!res.ok()) return setData('error', true)

        addAlert({
            type: 'success',
            subject: 'موفق',
            content: 'نتیجه درخواست با موفقیت ثبت شد',
            timeout: 3,
        })

        setState(
            produce(s => {
                let index = s.reqs.findIndex(i => i.id == id)

                if (index !== -1) {
                    s.reqs[index]!.state = res.body.state
                }
            })
        )

        close()
    }

    const Loading = () => {
        return (
            <div class='req-loading title_hero'>
                <LoadingDots />

                <span>درحال ثبت الحاقیه</span>
            </div>
        )
    }

    const Ctas = () => {
        return (
            <>
                <button
                    type='button'
                    class='req-cta title_smaller red'
                    disabled={req?.state == 'rejected'}
                    onclick={() => {
                        setPopup({
                            show: true,
                            Icon: CloseIcon,
                            title: 'درخواست را رد میکنید؟',
                            type: 'error',
                            onSubmit: () => submit('rejected'),
                        })
                    }}
                >
                    <CloseIcon />
                    رد درخواست
                </button>
                <button
                    type='button'
                    class='req-cta title_smaller load'
                    disabled={req?.state == 'in_progress'}
                    onclick={() =>
                        setPopup({
                            show: true,
                            Icon: WarningIcon,
                            title: 'درخواست را به حالت انتظار میارید؟ ',
                            onSubmit: () => submit('in_progress'),
                            type: 'info',
                        })
                    }
                >
                    <LoadingIcon />
                    حالت انتظار
                </button>
                <button
                    type='submit'
                    disabled={req?.state == 'done'}
                    class='req-cta title_smaller check'
                >
                    <CheckFillIcon />
                    حل درخواست
                </button>
            </>
        )
    }

    return (
        <div
            class='helmet-req-popup'
            classList={{ active: !!state.activeReq }}
            onclick={close}
        >
            <form
                onsubmit={e => {
                    e.preventDefault()

                    setPopup({
                        show: true,
                        Icon: CheckFillIcon,
                        title: 'درخواست را تایید میکنید؟',
                        type: 'success',
                        onSubmit: () => submit('done'),
                    })
                }}
                onclick={e => e.stopPropagation()}
                class='helmet-req-wrapper'
            >
                <div class='close-req icon' role='button' onclick={close}>
                    <CloseIcon />
                </div>

                <div class='req-header title_smaller'>
                    <div class='header-col'>
                        <div class='icon'>
                            <CorpsIcon />
                        </div>
                        <span>{cState.corp?.name || 'بدون نام'}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <UserIcon />
                        </div>
                        <span>{req?.user?.name || 'بدون نام'}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <ShieldCheckIcon />
                        </div>
                        <span>{req?.helmet}</span>
                    </div>
                    <div class='header-col'>
                        <div class='icon'>
                            <CalendarIcon />
                        </div>
                        <span>
                            {req &&
                                new Date(
                                    req.created_at * 1e3
                                ).toLocaleDateString('fa-IR')}
                        </span>
                    </div>
                </div>
                <div class='req-text-container'>
                    <div class='req-text'>
                        <div class='text-wrapper title_smaller'>
                            {req?.detail}
                        </div>
                    </div>
                </div>
                <Show when={isAdmin()}>
                    <div class='req-actions'>
                        <Ctas />
                    </div>
                </Show>

                <Show when={data.loading}>
                    <Loading />
                </Show>
            </form>
        </div>
    )
}

export default CorpExtentions
